import { useRef, useState } from "react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { useSimpleLine } from "./useSimpleLine";
import { NoteField, PriceBar, ReadyBy, Section, money } from "./Fields";

/** The Artwork card (5.3): upload the file, say what you need, pay the $24 cleanup. */
export default function ArtworkBody({ store, cleanupFee, placeholder, ready, image }) {
    const line = useSimpleLine({ store, cleanupFee: 0 });
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const add = async (list) => {
        if (!list?.length) return;
        setBusy(true);
        try {
            const recs = [];
            for (const f of Array.from(list)) recs.push(await uploadArtwork(f));
            setFiles((p) => [...p, ...recs]);
        } catch { toast.error("Upload failed. Try again."); } finally { setBusy(false); }
    };

    const build = async () => ({
        product_slug: "artwork-cleanup", product_name: "Artwork cleanup",
        image: files[0]?.content_type?.startsWith("image/") ? mediaUrl(files[0].url) : mediaUrl(image),
        quantity: 1, unit_price: Number(cleanupFee), line_total: Number(cleanupFee),
        summary: `${files.length} file${files.length === 1 ? "" : "s"}`, store,
        selections: { kind: "artwork", store_slug: store?.slug, ready_in: "days_3_5" },
        artwork: files,
    });

    return (
        <>
            <Section testid="design-section">
                <div className="relative h-[60vw] max-h-[360px] w-full border border-line bg-page" data-testid="artwork-stage">
                    {files.length ? (
                        <div className="flex h-full flex-wrap content-start gap-2 p-3" data-testid="artwork-files">
                            {files.map((f) => (
                                <span key={f.id} className="flex items-center gap-2 border border-ink px-2 py-1 text-[14px]">
                                    {f.content_type?.startsWith("image/") && <img src={mediaUrl(f.url)} alt="" className="h-10 w-10 object-contain" />}
                                    <span className="max-w-[140px] truncate">{f.filename || f.original_filename || "file"}</span>
                                    <button type="button" onClick={() => setFiles((p) => p.filter((x) => x.id !== f.id))} className="underline" aria-label="Remove file">×</button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        image && <img src={mediaUrl(image)} alt="" className="h-full w-full object-cover" />
                    )}
                    <button type="button" onClick={() => fileRef.current?.click()} className="absolute inset-x-6 bottom-6 flex min-h-[48px] items-center justify-center border border-ink bg-page text-[16px] font-semibold" data-testid="upload-file">
                        {busy ? "Uploading…" : files.length ? "Add another file" : "Upload file"}
                    </button>
                </div>
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.ai,.eps,.svg,.psd" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="upload-input" />
                <p className="mt-3 text-[16px]">We make your file sharp and print-ready for {money(cleanupFee)}. Bigger redraws get a pay link.</p>
            </Section>
            <NoteField value={line.note} onChange={line.setNote} placeholder={placeholder} />
            <ReadyBy store={store} readyIn="days_3_5" extraDays={0} fallback={ready} needBy={line.needBy} setNeedBy={line.setNeedBy} />
            <PriceBar total={Number(cleanupFee)} busy={line.busy || busy} errors={files.length ? [] : ["Upload your file"]} note="Artwork cleanup, flat"
                onPay={() => line.submit(build, "pay")} onAdd={() => line.submit(build, "cart")} />
        </>
    );
}
